import React, { useEffect, useRef, useState } from 'react';
import { Lightbulb, BarChart3, UserCheck, Rocket, Heart, CheckCircle } from 'lucide-react';
import { beliefData } from '../../data/mock';

const iconMap = {
  Lightbulb,
  BarChart3,
  UserCheck
};

const BeliefSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'var(--bg-page)' }}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: 'var(--brand-primary)' }} />
      
      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <Heart size={16} />
            <span className="font-semibold text-sm">The Zenova Belief</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {beliefData.title}
          </h2>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {beliefData.subtitle}
          </p>
        </div>

        {/* Beliefs Grid */}
        <div className="max-w-4xl mx-auto mb-12">
          <p 
            className={`body-medium text-center mb-8 ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`} 
            style={{ color: 'var(--text-primary)' }}
          >
            At Zenova, we believe:
          </p>
          <div className="space-y-4">
            {beliefData.beliefs.map((belief, index) => {
              const Icon = iconMap[belief.icon];
              return (
                <div 
                  key={index}
                  className={`flex items-center gap-4 p-5 rounded-2xl card hover-lift ${
                    isVisible ? 'animate-fadeInUp' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${(index + 3) * 100}ms`, background: 'white' }}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--brand-primary)' }}
                  >
                    <Icon size={24} style={{ color: 'var(--brand-dark)' }} />
                  </div>
                  <span className="heading-3" style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)' }}>
                    {belief.text}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Outcome Card */}
        <div 
          className={`max-w-3xl mx-auto card p-8 md:p-10 text-center ${
            isVisible ? 'animate-fadeInUp stagger-6' : 'opacity-0'
          }`}
          style={{ background: 'var(--brand-dark)' }}
        >
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: 'var(--brand-primary)' }}
          >
            <Rocket size={32} style={{ color: 'var(--brand-dark)' }} />
          </div>
          <p className="body-large mb-6" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            {beliefData.outcome}
          </p>
          <p className="heading-3" style={{ color: 'var(--brand-primary)' }}>
            {beliefData.closing}
          </p>
        </div>
      </div>
    </section>
  );
};

export default BeliefSection;
